import React, { useState, useEffect, useRef } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ReferenceLine, ResponsiveContainer,
} from 'recharts';
import { api } from '../hooks/useApi';
import Icon from '../components/Icon';
import './TemperaturesDashboard.css';

const COULEURS_PROBES = ['#c8941a', '#16a34a', '#2563eb', '#c0392b', '#9333ea', '#0891b2'];
const FENETRES = [
  { heures: 1,   label: '1 h' },
  { heures: 6,   label: '6 h' },
  { heures: 24,  label: '24 h' },
  { heures: 168, label: '7 j' },
];

function formaterHeure(dateStr) {
  if (!dateStr) return '--:--';
  return new Date(dateStr).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' });
}

function pointsEnMs(points) {
  return (points || []).map(p => ({ ...p, time: new Date(p.time).getTime() }));
}

function TempSkeleton() {
  return (
    <div className="temp-skeleton">
      <div className="skeleton skel-temp-title" />
      <div className="skeleton skel-temp-sub" />
      <div className="skeleton skel-temp-probes" />
      <div className="skeleton skel-temp-graph" />
    </div>
  );
}

/**
 * TemperaturesDashboard — page /temperatures
 */
export default function TemperaturesDashboard() {
  const [sondesLive, setSondesLive]             = useState([]);
  const [fenetreHeures, setFenetreHeures]       = useState(1);
  const [historique, setHistorique]             = useState([]);
  const [sessionsActives, setSessionsActives]   = useState([]);
  const [sessionSelectee, setSessionSelectee]   = useState(null);
  const [courbeSession, setCourbeSession]       = useState(null);
  const [chargement, setChargement]             = useState(true);
  const [erreur, setErreur]                     = useState(null);
  const [msg, setMsg]                           = useState(null);
  const fileInputRef                            = useRef(null);
  const intervalRef                             = useRef(null);

  async function chargerTout() {
    try {
      const [live, hist, actives] = await Promise.all([
        api.get('/meater/devices/live').catch(() => []),
        api.get(`/meater/lectures?heures=${fenetreHeures}`).catch(() => ({ series: [] })),
        api.get('/meater/sessions/active').catch(() => []),
      ]);
      setSondesLive(Array.isArray(live) ? live : []);
      setHistorique(Array.isArray(hist?.series) ? hist.series : []);
      setSessionsActives(Array.isArray(actives) ? actives : []);
      setErreur(null);

      if (Array.isArray(actives) && actives.length > 0) {
        setSessionSelectee(prev => prev && actives.find(s => s.id === prev) ? prev : actives[0].id);
      } else {
        setSessionSelectee(null);
        setCourbeSession(null);
      }
    } catch (e) {
      setErreur(e.response?.data?.error || e.message || 'Erreur de chargement');
    } finally {
      setChargement(false);
    }
  }

  useEffect(() => {
    chargerTout();
    intervalRef.current = setInterval(chargerTout, 30_000);
    return () => clearInterval(intervalRef.current);
  }, [fenetreHeures]); // eslint-disable-line

  useEffect(() => {
    if (!sessionSelectee) { setCourbeSession(null); return; }
    let annule = false;
    api.get(`/meater/sessions/${sessionSelectee}/courbe`)
      .then(data => { if (!annule) setCourbeSession(data); })
      .catch(() => { if (!annule) setCourbeSession(null); });
    return () => { annule = true; };
  }, [sessionSelectee, sessionsActives.length]);

  function ouvrirImport() {
    fileInputRef.current?.click();
  }

  async function importerFichier(e) {
    const fichier = e.target.files?.[0];
    e.target.value = '';
    if (!fichier) return;

    try {
      const texte = await fichier.text();
      const data  = JSON.parse(texte);
      if (!data.batch_id || !data.produit || !Array.isArray(data.points)) {
        alert('Fichier invalide : champs batch_id, produit et points[] requis.');
        return;
      }
      const res = await api.post('/meater/sessions/import', data);
      setMsg({ type: 'success', text: `Cuisson importée (session #${res.session_id}, ${res.nb_points_importes} points).` });
      chargerTout();
    } catch (err) {
      setMsg({ type: 'danger', text: 'Import échoué : ' + (err.response?.data?.error || err.message) });
    }
  }

  function exporterSession(sessionId, format = 'json') {
    const token = localStorage.getItem('escamel_token');
    fetch(`/api/meater/sessions/${sessionId}/export?format=${format}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const nomFichier = (r.headers.get('content-disposition') || '')
          .match(/filename="([^"]+)"/)?.[1] || `cuisson-${sessionId}.${format}`;
        return r.blob().then(blob => ({ blob, nomFichier }));
      })
      .then(({ blob, nomFichier }) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = nomFichier;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      })
      .catch(e => setMsg({ type: 'danger', text: 'Export échoué : ' + e.message }));
  }

  // ── Rendu ───────────────────────────────────────────────────────────
  if (chargement) return <TempSkeleton />;

  const sessionActive = sessionsActives.find(s => s.id === sessionSelectee);
  const seriesCourbe  = courbeSession?.series || [];

  return (
    <div className="temperatures-dashboard rise-in">

      {/* ── En-tête page ─────────────────────────────────────────────── */}
      <div className="temp-page-header">
        <div className="temp-page-header-text">
          <h1 className="temp-page-title">Températures en direct</h1>
          <span className="temp-page-subtitle">Rafraîchissement automatique toutes les 30 s</span>
        </div>
        <div className="temp-header-actions">
          <button className="btn btn-ghost" onClick={ouvrirImport}>
            <Icon name="televerser" size={15} />
            Importer une cuisson
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json,.json"
            style={{ display: 'none' }}
            onChange={importerFichier}
          />
        </div>
      </div>

      {/* ── Messages ─────────────────────────────────────────────────── */}
      {msg && (
        <div className={`temp-msg temp-msg--${msg.type}`}>
          <Icon name={msg.type === 'success' ? 'succes' : 'erreur'} size={16} />
          <span>{msg.text}</span>
          <button className="btn btn-ghost btn--sm temp-msg-close" onClick={() => setMsg(null)}>
            <Icon name="fermer" size={14} />
          </button>
        </div>
      )}

      {erreur && (
        <div className="error-inline">
          <Icon name="erreur" size={16} />
          {erreur}
        </div>
      )}

      {/* ── Sondes en direct ─────────────────────────────────────────── */}
      <div className="card">
        <div className="meater-section-header">
          <div className="meater-section-left">
            <span className="meater-section-icon"><Icon name="temperature" size={16} /></span>
            <h3 className="meater-section-titre">Sondes en direct</h3>
          </div>
          <div className="meater-section-actions">
            <button
              className="btn btn-ghost btn--sm"
              onClick={async () => {
                try {
                  await api.post('/meater/devices/refresh');
                  chargerTout();
                } catch (e) {
                  setMsg({ type: 'danger', text: 'Rafraîchissement échoué : ' + (e?.response?.data?.error || e.message) });
                }
              }}
            >
              <Icon name="rafraichir" size={14} />
              Rafraîchir
            </button>
          </div>
        </div>

        {sondesLive.length === 0 ? (
          <div className="meater-aucune-session">
            <Icon name="temperature" size={32} />
            <div className="meater-aucune-session__titre">Aucune sonde détectée</div>
            <span>
              Vérifiez que l'email MEATER est configuré (Paramètres &rarr; MEATER)
              et que <code>MEATER_PASSWORD</code> est présent côté serveur.
              Les sondes doivent aussi être allumées.
            </span>
          </div>
        ) : (
          <div className="probes-grid">
            {sondesLive.map((s, i) => {
              const estHaute = typeof s.temp_interne === 'number' && s.temp_interne > 90;
              return (
                <div
                  key={s.device_id}
                  className="probe-live-card"
                  style={{ borderLeftColor: COULEURS_PROBES[i % COULEURS_PROBES.length] }}
                >
                  <div className="probe-live-nom">{s.nom}</div>
                  <div className="probe-live-temps">
                    <div className="probe-live-valeur">
                      <span className="probe-live-label">
                        <Icon name="viande" size={11} />
                        Interne
                      </span>
                      <span
                        className="probe-live-chiffre"
                        style={{ color: estHaute ? 'var(--danger)' : 'var(--success)' }}
                      >
                        {typeof s.temp_interne === 'number' ? `${s.temp_interne.toFixed(1)}°C` : '--'}
                      </span>
                    </div>
                    <div className="probe-live-valeur">
                      <span className="probe-live-label">
                        <Icon name="fumage" size={11} />
                        Fumoir
                      </span>
                      <span className="probe-live-chiffre">
                        {typeof s.temp_ambiante === 'number' ? `${s.temp_ambiante.toFixed(1)}°C` : '--'}
                      </span>
                    </div>
                  </div>
                  {s.cook_state && (
                    <div className="probe-live-state">{s.cook_state}</div>
                  )}
                  <div className="probe-live-maj">
                    Maj : {formaterHeure(s.timestamp)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Historique global ────────────────────────────────────────── */}
      <div className="card">
        <div className="meater-section-header">
          <div className="meater-section-left">
            <span className="meater-section-icon"><Icon name="courbe" size={16} /></span>
            <h3 className="meater-section-titre">Historique global</h3>
          </div>
          <div className="fenetre-tabs">
            {FENETRES.map(f => (
              <button
                key={f.heures}
                className={`session-tab${fenetreHeures === f.heures ? ' actif' : ''}`}
                onClick={() => setFenetreHeures(f.heures)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <p className="meater-graphe-meta">
          Lectures enregistrées en continu toutes les 30 s (historique conservé 7 j).
          Les 2 capteurs par sonde (interne = viande, ambiante = fumoir) sont tracés séparément.
        </p>

        {historique.length === 0 ? (
          <div className="empty-state">
            <Icon name="courbe" size={32} />
            <span>Aucune donnée dans la fenêtre choisie.</span>
          </div>
        ) : (
          <div style={{ height: 380 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis
                  dataKey="time"
                  type="number"
                  domain={['dataMin', 'dataMax']}
                  tickFormatter={v => new Date(v).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })}
                  stroke="var(--muted)"
                  tick={{ fontSize: 11 }}
                  allowDuplicatedCategory={false}
                />
                <YAxis
                  stroke="var(--muted)"
                  tick={{ fontSize: 11 }}
                  label={{ value: '°C', angle: -90, position: 'insideLeft', fill: 'var(--muted)', fontSize: 11 }}
                  domain={[0, 300]}
                />
                <Tooltip
                  contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', fontSize: 12, color: 'var(--text)' }}
                  labelFormatter={v => new Date(v).toLocaleString('fr-CA', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}
                  formatter={(val, name) => [`${typeof val === 'number' ? val.toFixed(1) : '--'}°C`, name]}
                />
                <Legend />
                <ReferenceLine
                  y={95}
                  stroke="var(--danger)"
                  strokeDasharray="5 5"
                  label={{ value: 'Cible 95°C', fill: 'var(--danger)', fontSize: 11 }}
                />
                {historique.map((serie, i) => {
                  const pts = pointsEnMs(serie.points);
                  return (
                    <React.Fragment key={serie.device_id}>
                      <Line
                        data={pts}
                        dataKey="temp_interne"
                        name={`${serie.nom} — interne`}
                        stroke={COULEURS_PROBES[i % COULEURS_PROBES.length]}
                        strokeWidth={2}
                        dot={false}
                        connectNulls
                      />
                      <Line
                        data={pts}
                        dataKey="temp_ambiante"
                        name={`${serie.nom} — fumoir`}
                        stroke={COULEURS_PROBES[i % COULEURS_PROBES.length]}
                        strokeWidth={1}
                        strokeDasharray="4 4"
                        dot={false}
                        connectNulls
                      />
                    </React.Fragment>
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* ── Cuissons en cours ────────────────────────────────────────── */}
      {sessionsActives.length > 0 && (
        <div className="card">
          <div className="meater-section-header">
            <div className="meater-section-left">
              <span className="meater-section-icon"><Icon name="fumage" size={16} /></span>
              <h3 className="meater-section-titre">Cuissons en cours</h3>
            </div>
          </div>

          {sessionsActives.length > 1 && (
            <div className="sessions-tabs">
              {sessionsActives.map(s => (
                <button
                  key={s.id}
                  className={`session-tab${sessionSelectee === s.id ? ' actif' : ''}`}
                  onClick={() => setSessionSelectee(s.id)}
                >
                  {s.batch_nom || s.produit} — #{s.id}
                </button>
              ))}
            </div>
          )}

          {sessionActive && (
            <div className="meater-graphe-card" style={{ marginTop: 0, border: 'none', boxShadow: 'none', padding: 0 }}>
              <div className="meater-graphe-header">
                <h4 className="meater-graphe-titre">
                  Courbe — {sessionActive.batch_nom || sessionActive.produit}
                </h4>
                <div className="meater-graphe-actions">
                  <button className="btn btn-ghost btn--sm" onClick={() => exporterSession(sessionActive.id, 'json')}>
                    <Icon name="telecharger" size={14} />
                    Export JSON
                  </button>
                  <button className="btn btn-ghost btn--sm" onClick={() => exporterSession(sessionActive.id, 'csv')}>
                    <Icon name="telecharger" size={14} />
                    Export CSV
                  </button>
                </div>
              </div>
              <p className="meater-graphe-meta">
                Début : {new Date(sessionActive.debut_cuisson).toLocaleString('fr-CA')}
                {' · '}
                {sessionActive.nb_points} points enregistrés
                {' · '}
                Temp. max interne : {typeof sessionActive.temp_max_interne === 'number' ? `${sessionActive.temp_max_interne.toFixed(1)}°C` : '--'}
              </p>

              {seriesCourbe.length === 0 ? (
                <div className="empty-state">
                  <Icon name="courbe" size={28} />
                  <span>Aucun point enregistré pour cette session.</span>
                </div>
              ) : (
                <div style={{ height: 350 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                      <XAxis
                        dataKey="time"
                        type="number"
                        domain={['dataMin', 'dataMax']}
                        tickFormatter={v => new Date(v).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })}
                        stroke="var(--muted)"
                        tick={{ fontSize: 11 }}
                        allowDuplicatedCategory={false}
                      />
                      <YAxis
                        stroke="var(--muted)"
                        tick={{ fontSize: 11 }}
                        label={{ value: '°C', angle: -90, position: 'insideLeft', fill: 'var(--muted)', fontSize: 11 }}
                        domain={[0, 300]}
                      />
                      <Tooltip
                        contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', fontSize: 12, color: 'var(--text)' }}
                        labelFormatter={v => new Date(v).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })}
                        formatter={(val, name) => [`${typeof val === 'number' ? val.toFixed(1) : '--'}°C`, name]}
                      />
                      <Legend />
                      <ReferenceLine
                        y={95}
                        stroke="var(--danger)"
                        strokeDasharray="5 5"
                        label={{ value: 'Cible 95°C', fill: 'var(--danger)', fontSize: 11 }}
                      />
                      {seriesCourbe.map((serie, i) => {
                        const pts = pointsEnMs(serie.points);
                        return (
                          <React.Fragment key={serie.device_id}>
                            <Line
                              data={pts}
                              dataKey="temp_interne"
                              name={`${serie.nom} — interne`}
                              stroke={COULEURS_PROBES[i % COULEURS_PROBES.length]}
                              strokeWidth={2}
                              dot={false}
                              connectNulls
                            />
                            <Line
                              data={pts}
                              dataKey="temp_ambiante"
                              name={`${serie.nom} — fumoir`}
                              stroke={COULEURS_PROBES[i % COULEURS_PROBES.length]}
                              strokeWidth={1}
                              strokeDasharray="4 4"
                              dot={false}
                              connectNulls
                            />
                          </React.Fragment>
                        );
                      })}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
